import { useState } from "react";
import "./whyUsTrustMatrix.css";

const trustPillars = [
  {
    id: "cost",
    number: "01",
    label: "Cost Efficiency",
    title: "Cost Efficiency & Transparency",
    description:
      "Clear pricing, measurable productivity, and visibility throughout delivery.",
    icon: "↗",
    points: [
      "Volume-based and per-image/hourly pricing",
      "Pay only for productive work, lower TCO",
      "Daily end-of-day reporting on targets",
      "Dedicated on-call managers, fast communication",
      "Full transparency at every stage",
    ],
  },
  {
    id: "domain",
    number: "02",
    label: "Domain Expertise",
    title: "Domain Depth (20+ industries)",
    description:
      "Specialized knowledge across diverse industries and AI applications.",
    icon: "◈",
    points: [
      "Precision Agriculture & Livestock",
      "Automotive & ADAS",
      "Generative AI (LLM, RLHF, Image, Video & Speech)",
      "Healthcare, Medical & Biological",
      "Retail, Fashion, Robotics, Security & Surveillance, and more",
    ],
  },
  {
    id: "workforce",
    number: "03",
    label: "Workforce & Delivery",
    title: "Workforce Training & Delivery",
    description:
      "A trained, dedicated in-house workforce supported throughout every engagement.",
    icon: "◎",
    points: [
      "100% in-house, no outsourcing, no WFH",
      "Handpicked team, 100 hrs in-house training",
      "Dedicated trainers, QA, leads and managers on every engagement",
      "Trained in work ethics, workplace communication and English",
    ],
  },
  {
    id: "quality",
    number: "04",
    label: "Quality & Security",
    title: "Quality, Security & Compliance",
    description:
      "Quality-led processes and security-focused delivery practices.",
    icon: "◇",
    points: [
      "Six Sigma-driven quality processes",
      "ISO, GDPR, HIPAA, TISAX compliant",
      "Enterprise-grade data security and delivery",
      "Managers validate output before client billing; pay only for productive hours",
    ],
  },
  {
    id: "scale",
    number: "05",
    label: "Speed & Scalability",
    title: "Speed & Scalability",
    description:
      "Responsive operations designed to support changing project requirements.",
    icon: "↗",
    points: [
      "24x7x365 team availability",
      "Scale on demand, rapid scale up, no onboarding delay",
      "Team deployed within 24 hours",
      "High operational agility, client-customized processes",
    ],
  },
  {
    id: "track-record",
    number: "06",
    label: "Our Track Record",
    title: "Scale & Track Record",
    description:
      "Experience, delivery capacity, and a track record of supporting global businesses.",
    icon: "⌁",
    points: [
      "12+ years in human-in-the-loop AI infrastructure",
      "950M+ images processed since 2014",
      "400+ full-time, in-house professionals",
      "Trusted by Fortune 500 and global tech leaders",
    ],
  },
];

export default function WhyUsTrustMatrix() {
  const [activeId, setActiveId] = useState("cost");

  const activePillar = trustPillars.find(
    (pillar) => pillar.id === activeId
  );

  return (
    <section className="why-us-trust-matrix">
      <div className="why-us-trust-matrix__container">
        <header className="why-us-trust-matrix__header">
          <div className="why-us-trust-matrix__eyebrow">
            <span
              className="why-us-trust-matrix__eyebrow-dot"
              aria-hidden="true"
            />
            THE QUALITAS ADVANTAGE
          </div>

          <div className="why-us-trust-matrix__heading-row">
            <h2 className="why-us-trust-matrix__title">
              Why Global AI Companies
              <br className="why-us-trust-matrix__desktop-break" />
              {" "}
              <span>Trust Qualitas</span>
            </h2>

            <p className="why-us-trust-matrix__intro">
              Six reasons businesses choose us to support their AI
              operations, from specialist expertise to scalable delivery.
            </p>
          </div>
        </header>

        <div className="why-us-trust-matrix__layout">
          <nav
            className="why-us-trust-matrix__navigation"
            aria-label="Qualitas trust pillars"
          >
            {trustPillars.map((pillar) => {
              const isActive = activeId === pillar.id;

              return (
                <button
                  key={pillar.id}
                  type="button"
                  className={`why-us-trust-matrix__item ${
                    isActive
                      ? "why-us-trust-matrix__item--active"
                      : ""
                  }`}
                  onClick={() => setActiveId(pillar.id)}
                  aria-pressed={isActive}
                >
                  <span className="why-us-trust-matrix__item-top">
                    <span className="why-us-trust-matrix__item-number">
                      {pillar.number}
                    </span>

                    <span
                      className="why-us-trust-matrix__item-arrow"
                      aria-hidden="true"
                    >
                      ↗
                    </span>
                  </span>

                  <span className="why-us-trust-matrix__item-label">
                    {pillar.label}
                  </span>

                  <span className="why-us-trust-matrix__item-indicator" />
                </button>
              );
            })}
          </nav>

          <div
            className="why-us-trust-matrix__details"
            aria-live="polite"
            aria-atomic="true"
          >
            <div className="why-us-trust-matrix__details-top">
              <span className="why-us-trust-matrix__details-number">
                {activePillar.number}
              </span>

              <span className="why-us-trust-matrix__details-tag">
                TRUST PILLAR
              </span>
            </div>

            <h3 className="why-us-trust-matrix__details-title">
              {activePillar.title}
            </h3>

            <p className="why-us-trust-matrix__details-description">
              {activePillar.description}
            </p>

            <ul className="why-us-trust-matrix__points">
              {activePillar.points.map((point, index) => (
                <li key={`${activePillar.id}-${index}`}>
                  <span
                    className="why-us-trust-matrix__point-marker"
                    aria-hidden="true"
                  />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <div className="why-us-trust-matrix__details-footer">
              <span>QUALITAS GLOBAL</span>
              <span>
                {activePillar.number} / 06
              </span>
            </div>
          </div>
        </div>

        <div className="why-us-trust-matrix__takeaway">
          <div className="why-us-trust-matrix__takeaway-label">
            <span
              className="why-us-trust-matrix__takeaway-mark"
              aria-hidden="true"
            >
              Q
            </span>
            <span>THE BIG PICTURE</span>
          </div>

          <p className="why-us-trust-matrix__takeaway-text">
            Qualitas is your long-term AI operations partner, delivering
            enterprise-grade quality, security, reliability, and scalable
            execution as your AI evolves.
          </p>
        </div>
      </div>
    </section>
  );
}